import { admin, allOf, anyOf, exception, internal, job, signedIn } from './domain/rules';

export const EVERYONE = signedIn;

export const STAFF_ONLY = internal(signedIn);

export const PLANT_RULES = {
  list: job('core.plantDetails.read'),
  manage: admin('core.siteSetup.plant'),
  configure: job('core.plantDetails.update'),
  documents: job('core.plantDetails.read'),
  readPlcs: job('core.plc.read'),
  addPlc: job('core.plc.create'),
  deletePlc: job('core.plc.delete'),
  sensors: admin('iot.sensorConfig.sensors'),
  dataInputConfig: anyOf(job('data.labEntry.update'), job('ops.shifts.update')),
  escalation: admin('ops.rules.routing'),
  alertRules: admin('ops.rules.manage'),
  dataBreak: admin('platform.governance.library'),
  companies: admin('platform.governance.onboard'),
  manageLicences: admin('core.siteSetup.licence'),
  // GET /v2/audit?changeType=LICENCE_CHANGE and GET /v2/audit/plants: requireAnyAdmin(['people.access.export', 'core.siteSetup.plant']).
  licenceHistory: anyOf(admin('core.siteSetup.plant'), admin('people.access.export')),
} as const;

export const DASHBOARD_RULES = {
  view: job('core.reports.read'),
  edit: allOf(job('core.reports.read'), admin('core.config.manage')),
  events: job('analytics.events.read'),
  correctData: job('data.corrections.apply'),
  deleteFlocSample: STAFF_ONLY,
} as const;

export const SCADA_RULES = {
  view: job('scada.visualisation.read'),
  author: admin('core.config.manage'),
  edit: allOf(job('scada.visualisation.read'), admin('core.config.manage')),
  actuate: exception('REMOTE_ACTUATION'),
} as const;

export const SENSOR_HEALTH_RULES = {
  view: exception('SENSOR_HEALTH'),
  configure: admin('iot.sensorConfig.sensors'),
} as const;

export const ISSUE_RULES = {
  view: job('ops.issueSessions.read'),
  report: job('ops.issues.report'),
} as const;

export const TASK_RULES = {
  shifts: job('ops.shifts.read'),
  planShifts: job('ops.shifts.create'),
  deleteRoster: job('ops.shifts.delete'),
  deleteTask: job('ops.shifts.delete'),
  templates: job('ops.templates.read'),
  createTemplate: job('ops.templates.create'),
  deleteTemplate: job('ops.templates.delete'),
  workflows: job('ops.workflows.read'),
  createWorkflow: job('ops.workflows.create'),
  deleteWorkflow: job('ops.workflows.delete'),
  skills: admin('people.access.skills'),
} as const;

export const ANALYTICS_RULES = {
  insights: job('ops.insights.read'),
  createInsight: job('ops.insights.create'),
  deleteInsight: job('ops.insights.delete'),
  events: job('analytics.events.read'),
  eventTemplates: job('analytics.eventTemplates.read'),
  createEventTemplate: job('analytics.eventTemplates.create'),
  deleteEventTemplate: job('analytics.eventTemplates.delete'),
  triggers: job('analytics.triggers.read'),
  createTrigger: job('analytics.triggers.create'),
  deleteTrigger: anyOf(job('analytics.triggers.delete'), admin('ops.rules.manage')),
  formulas: admin('analytics.formulas.manage'),
  manageDataLogger: job('iot.logger.update'),
  batches: job('core.reports.read'),
  manageBatches: admin('core.config.manage'),
} as const;

export const DATA_INPUT_RULES = {
  read: job('data.labEntry.read'),
  enter: anyOf(job('data.labEntry.create'), job('data.labEntry.update'), job('ops.shifts.update')),
  backdate: job('data.labEntry.update'),
  templates: job('data.inputTemplates.read'),
  createTemplate: job('data.inputTemplates.create'),
  deleteTemplate: job('data.inputTemplates.delete'),
  logbooks: job('data.logbookConfig.read'),
  createLogbook: job('data.logbookConfig.create'),
  logbookTemplates: job('data.logbookTemplates.read'),
  createLogbookTemplate: job('data.logbookTemplates.create'),
  deleteLogbookTemplate: job('data.logbookTemplates.delete'),
  deleteLogbook: job('data.logbookConfig.delete'),
  record: job('data.labEntry.create'),
  logbookEntry: anyOf(job('data.labEntry.create'), job('data.labEntry.update')),
  labResults: anyOf(job('data.labEntry.create'), job('data.labEntry.update')),
  maintenance: job('ops.shifts.update'),
} as const;

export const INVENTORY_RULES = {
  stock: job('inv.stock.read'),
  useStock: job('inv.stock.update'),
  movementLogs: job('inv.movementLogs.view'),
  catalog: admin('inv.catalog.manage'),
  backdate: STAFF_ONLY,
} as const;

const invitePeople = admin('people.access.invite');

export const PEOPLE_RULES = {
  manageAccess: admin('people.access.grants'),
  invite: invitePeople,
  readAudit: admin('people.access.export'),
  userList: internal(invitePeople),
  searchPeople: invitePeople,
  viewAs: exception('VIEW_AS_USER'),
  siteSettings: anyOf(admin('ops.rules.routing'), admin('people.access.notifications')),
} as const;

export const GOVERNANCE_RULES = {
  systemPerformance: admin('platform.governance.library'),
} as const;
